import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, MapPin, Clock, Star, Phone, WhatsappLogo, Camera, ForkKnife, Buildings } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";

const whatsappMsg = encodeURIComponent("Bonjour, je souhaite organiser une journée Paris sur mesure avec chauffeur privé ALTURA. Pouvez-vous me faire un devis ?");
const whatsappUrl = `https://wa.me/33681969342?text=${whatsappMsg}`;

const ParisSurMesurePage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": "Paris Sur Mesure avec Chauffeur Privé — ALTURA",
    "description": "Découvrez Paris autrement avec un chauffeur privé dédié. Itinéraire personnalisé : monuments, quartiers secrets, gastronomie, croisière sur la Seine.",
    "url": "https://alturaconciergerie.com/experiences/paris-sur-mesure",
    "touristType": "Luxury",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ALTURA Conciergerie",
      "telephone": "+33681969342",
    },
  };

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="paris-sur-mesure-page">
      <Helmet>
        <title>Paris Sur Mesure avec Chauffeur Privé | Visite Privée Paris | ALTURA</title>
        <meta name="description" content="Visitez Paris à votre rythme avec un chauffeur privé ALTURA. Tour Eiffel, Louvre, Montmartre, croisière Seine, gastronomie. Itinéraire 100% personnalisé." />
        <link rel="canonical" href="https://alturaconciergerie.com/experiences/paris-sur-mesure" />
        <meta property="og:title" content="Paris Sur Mesure — Chauffeur Privé | ALTURA" />
        <meta property="og:description" content="Découvrez Paris autrement. Itinéraire personnalisé avec chauffeur privé de luxe." />
        <meta property="og:url" content="https://alturaconciergerie.com/experiences/paris-sur-mesure" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.pexels.com/photos/34773158/pexels-photo-34773158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[450px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/34773158/pexels-photo-34773158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Tour Eiffel vue panoramique — Paris sur mesure ALTURA"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EDEBE4] via-[#EDEBE4]/40 to-[#1A1A1A]/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/70 mb-3">ALTURA · Expériences</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight">
                Paris sur mesure
              </h1>
              <p className="text-[#1A1A1A]/70 text-base mt-4 max-w-xl font-light">
                Votre Paris, votre rythme. Un itinéraire entièrement conçu selon vos envies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro + CTA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-6">Découvrez Paris autrement</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-6">
                Paris ne se visite pas, elle se vit. Avec ALTURA, explorez la Ville Lumière à votre rythme, guidé par un chauffeur privé qui connaît chaque recoin de la capitale. Des grands monuments aux passages secrets, des restaurants étoilés aux bistrots authentiques — votre journée est entièrement conçue sur mesure.
              </p>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">
                Que vous soyez en couple, en famille ou entre amis, nous adaptons le parcours à vos envies : culture, gastronomie, architecture, shopping ou simplement la flânerie parisienne dans le confort d'une berline de prestige.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="paris-cta-whatsapp">
                  <WhatsappLogo size={18} weight="fill" /> Devis sur mesure
                </a>
                <Link to="/contact" className="border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A] hover:text-[#EDEBE4] transition-colors inline-flex items-center gap-2" data-testid="paris-cta-contact">
                  <Phone size={16} /> Nous contacter
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <img
                src="https://images.unsplash.com/photo-1500039436846-25ae2f11882e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Musée du Louvre — visite privée Paris ALTURA"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Formules */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">Nos formules</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">Votre journée idéale à Paris</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Camera size={28} weight="thin" />, title: "Paris Classique", desc: "Tour Eiffel, Arc de Triomphe, Champs-Élysées, Louvre, Notre-Dame, Montmartre. Les incontournables de la capitale dans un circuit optimisé." },
              { icon: <Buildings size={28} weight="thin" />, title: "Paris Secret", desc: "Le Marais, passages couverts, Île Saint-Louis, jardin du Luxembourg, rues pavées de Saint-Germain. Un Paris loin des foules touristiques." },
              { icon: <ForkKnife size={28} weight="thin" />, title: "Paris Gastronomique", desc: "Marchés, boulangeries d'exception, déjeuner dans un restaurant étoilé, dégustation de vins. Une journée pour les épicuriens." },
              { icon: <Star size={28} weight="thin" />, title: "Paris Romantique", desc: "Croisière sur la Seine au coucher du soleil, Pont des Arts, Montmartre, dîner en tête-à-tête. Le Paris des amoureux." },
              { icon: <Compass size={28} weight="thin" />, title: "Paris by Night", desc: "Découvrez Paris illuminé : Tour Eiffel scintillante, Champs-Élysées, Pont Alexandre III, Sacré-Cœur. Magie nocturne garantie." },
              { icon: <MapPin size={28} weight="thin" />, title: "Sur mesure total", desc: "Vous avez vos propres envies ? Partagez-les avec nous et nous construisons ensemble un programme 100% personnalisé." },
            ].map((formula, i) => (
              <motion.div
                key={formula.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-6"
              >
                <div className="text-[#1A1A1A] mb-4">{formula.icon}</div>
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{formula.title}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{formula.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme type */}
      <section className="py-16 sm:py-20 bg-[#EDEBE4]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-8">Exemple de journée Paris Classique</h2>
            <div className="space-y-6">
              {[
                { time: "9h00", title: "Prise en charge", desc: "Votre chauffeur vous accueille à votre hôtel ou domicile parisien. Café et journaux à bord sur demande." },
                { time: "9h30", title: "Montmartre & Sacré-Cœur", desc: "Balade dans les ruelles pittoresques, Place du Tertre, vue panoramique depuis le parvis du Sacré-Cœur." },
                { time: "11h00", title: "Opéra & Grands Boulevards", desc: "Passage devant l'Opéra Garnier, les Galeries Lafayette, et les passages couverts du IXe arrondissement." },
                { time: "12h30", title: "Déjeuner", desc: "Pause gastronomique dans un restaurant sélectionné selon vos préférences : étoilé, bistronomique ou terrasse avec vue." },
                { time: "14h30", title: "Rive Gauche", desc: "Saint-Germain-des-Prés, le jardin du Luxembourg, le Panthéon, le Quartier Latin." },
                { time: "16h00", title: "Île de la Cité", desc: "Notre-Dame, Sainte-Chapelle, Pont Neuf. Le cœur historique de Paris." },
                { time: "17h30", title: "Tour Eiffel & Trocadéro", desc: "Les plus belles vues de Paris. Temps libre pour photos et visite." },
                { time: "19h00", title: "Retour", desc: "Retour à votre hôtel ou vers le restaurant de votre choix pour la soirée." },
              ].map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex gap-6 items-start"
                >
                  <div className="w-16 flex-shrink-0 text-right">
                    <p className="text-[#1A1A1A] text-sm font-medium">{step.time}</p>
                  </div>
                  <div className="w-px bg-[#1A1A1A]/20 min-h-[60px] flex-shrink-0 relative">
                    <div className="absolute top-1 -left-[3px] w-[7px] h-[7px] bg-[#1A1A1A] rounded-full" />
                  </div>
                  <div className="flex-1 pb-4">
                    <h3 className="text-[#1A1A1A] font-medium text-sm mb-1">{step.title}</h3>
                    <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-10">Questions fréquentes</h2>
          <div className="space-y-6">
            {[
              { q: "Combien coûte une journée Paris sur mesure ?", a: "Le tarif dépend du véhicule choisi et de la durée de mise à disposition (demi-journée ou journée complète). Contactez-nous pour un devis personnalisé adapté à votre programme." },
              { q: "Peut-on modifier l'itinéraire en cours de journée ?", a: "Absolument. Votre chauffeur s'adapte en temps réel à vos envies. C'est l'avantage d'un service 100% sur mesure : aucune contrainte, que du plaisir." },
              { q: "Le chauffeur peut-il faire office de guide ?", a: "Nos chauffeurs connaissent Paris parfaitement et peuvent vous orienter et recommander les meilleures adresses. Pour un guide professionnel certifié, nous pouvons organiser un accompagnement sur demande." },
              { q: "Quels véhicules sont disponibles ?", a: "Mercedes Classe E, Classe S, Classe V (7 places), Range Rover, Classe G ou Maybach. Le choix dépend de vos préférences et du nombre de passagers." },
              { q: "Proposez-vous des expériences en soirée ?", a: "Oui, notre formule Paris by Night est très demandée. Découvrez Paris illuminé avec un itinéraire spécialement pensé pour profiter des lumières de la capitale." },
            ].map((item, i) => (
              <motion.div key={item.q} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h3 className="text-[#1A1A1A] font-medium text-sm mb-2">{item.q}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#EDEBE4] mb-4">Créez votre Paris idéal</h2>
          <p className="text-[#EDEBE4]/60 text-sm mb-8">Dites-nous vos envies, nous créons l'itinéraire parfait.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="paris-cta-bottom-wa">
              <WhatsappLogo size={18} weight="fill" /> Devis WhatsApp
            </a>
            <a href="tel:+33681969342" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-4 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="paris-cta-bottom-call">
              <Phone size={16} /> +33 6 81 96 93 42
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ParisSurMesurePage;
